import React, { useCallback, useEffect, useState } from "react";
import { API, CALENDLY_TZ, TimePicker, fmtDay, fmtTime } from "./bookingParts";
import "./booking.css";

const post = (id, action, body) => fetch(`${API}/api/public/booking/confirmed/${encodeURIComponent(id)}/${action}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
  .then(async (r) => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }));

// Reschedule / cancel without leaving the site. mode: null | "reschedule" | "cancel"
export function ManageBooking({ booking, onUpdated }) {
  const [mode, setMode] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsState, setSlotsState] = useState({ loading: false, error: null });
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const cancelled = booking.status === "cancelled";
  const ctxKey = booking.context?.key || "buying";

  const loadSlots = useCallback(() => {
    setSlotsState({ loading: true, error: null });
    fetch(`${API}/api/public/booking/availability?context=${ctxKey}&days=14`).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || "calendar"); return r.json(); })
      .then((d) => { setSlots((d.slots || []).filter((s) => s !== booking.start_time)); setSlotsState({ loading: false, error: null }); })
      .catch((e) => setSlotsState({ loading: false, error: e.message || "Couldn't load the calendar." }));
  }, [ctxKey, booking.start_time]);
  useEffect(() => { if (mode === "reschedule" && !slots.length && !slotsState.loading && !slotsState.error) loadSlots(); }, [mode, slots.length, slotsState, loadSlots]);

  const open = (m) => { setMode(m); setSlot(null); setError(null); setDone(null); };
  const close = () => { setMode(null); setSlot(null); setError(null); };

  const confirmReschedule = async () => {
    setBusy(true); setError(null);
    const r = await post(booking.booking_id, "reschedule", { start_time: slot, timezone: CALENDLY_TZ });
    setBusy(false);
    if (!r.ok) { setError(r.data.detail || "Something went sideways. Try again or text us."); if (r.status === 409) { setSlot(null); setSlots([]); } return; }
    onUpdated(r.data); setMode(null); setSlot(null); setSlots([]); setDone(cancelled ? "rebooked" : "moved");
  };
  const confirmCancel = async () => {
    setBusy(true); setError(null);
    const r = await post(booking.booking_id, "cancel", {});
    setBusy(false);
    if (!r.ok) { setError(r.data.detail || "Something went sideways. Try again or text us."); return; }
    onUpdated(r.data); setMode(null); setDone("cancelled");
  };

  return (
    <div className="bkd-manage" data-testid="booked-manage">
      {done === "moved" && <div className="bkd-manage-ok" role="status" data-testid="booked-moved">Done — your call has been moved. Your calendar invitation will update too.</div>}
      {done === "rebooked" && <div className="bkd-manage-ok" role="status" data-testid="booked-rebooked">You’re back on the calendar. A new confirmation is on its way.</div>}
      {done === "cancelled" && <div className="bkd-manage-ok" role="status" data-testid="booked-cancelled-note">Your call is cancelled. No hard feelings — pick a new time whenever you’re ready.</div>}
      {!mode && (
        <p className="bkd-fine bkd-move" data-testid="booked-manage-links">
          {cancelled ? <>Changed your mind? <button type="button" className="bkd-textbtn" onClick={() => open("reschedule")} data-testid="booked-rebook">Pick a new time</button></>
            : <>Need another time? <button type="button" className="bkd-textbtn" onClick={() => open("reschedule")} data-testid="booked-reschedule">Reschedule</button> · <button type="button" className="bkd-textbtn" onClick={() => open("cancel")} data-testid="booked-cancel">Cancel</button></>}
        </p>
      )}
      {mode === "cancel" && (
        <div className="bkd-manage-box" data-testid="booked-cancel-confirm">
          <p><b>Cancel this call?</b> We’ll let the team know, and you can always book again later.</p>
          {error && <div className="bkd-phone-err" role="alert">{error}</div>}
          <div className="bkd-manage-actions"><button type="button" className="btn outline" onClick={confirmCancel} disabled={busy} data-testid="booked-cancel-yes">{busy ? "Cancelling…" : "Yes, cancel my call"}</button><button type="button" className="bkd-textbtn" onClick={close} data-testid="booked-cancel-keep">Keep my call</button></div>
        </div>
      )}
      {mode === "reschedule" && (
        <div className="bkd-manage-box bk-widget compact" data-testid="booked-reschedule-panel">
          {!slot && <>
            <TimePicker slots={slots} value={slot} onPick={setSlot} loading={slotsState.loading} error={slotsState.error} onRetry={loadSlots} duration={booking.duration || 15} />
            <button type="button" className="bkd-textbtn" onClick={close} data-testid="booked-reschedule-back">{cancelled ? "Not right now" : "Keep my current time"}</button>
          </>}
          {slot && (
            <div data-testid="booked-reschedule-confirm">
              <p className="bk-picked">{fmtDay(slot)} · {fmtTime(slot)} <button type="button" className="textlink" onClick={() => setSlot(null)} data-testid="booked-reschedule-change">Change</button></p>
              <p>{cancelled ? "Book this time?" : "Move your call to this time? Your original time will be released."}</p>
              {error && <div className="bkd-phone-err" role="alert" data-testid="booked-reschedule-error">{error}</div>}
              <div className="bkd-manage-actions"><button type="button" className="btn" onClick={confirmReschedule} disabled={busy} data-testid="booked-reschedule-yes">{busy ? "Saving…" : cancelled ? "Book this time" : "Confirm new time"}</button><button type="button" className="bkd-textbtn" onClick={close} data-testid="booked-reschedule-cancel">Never mind</button></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
